import React from "react";
import { Mail, FileText, Crown, Star } from "lucide-react";
import ApplicationTags from "./ApplicationTags";
import ApplicationFields from "./ApplicationFields";
import ApplicationHistory from "./ApplicationHistory";
import CandidateCV from "./CandidateCV";
import JobDetail from "./JobDetail";
import CandidateScore from "./CandidateScore";
import CandidateNotes from "@/pages/PreScreen/CandidateNotes";

function CurrentApplicationView({
  direction = "",
  currentApplication,
  currentVacancy,
  tagsPerApplication,
  availableTags,
  onAddTag,
  onRemoveTag,
  showCV,
  onToggleCV,
  onScoreChange,
  onAddNote,
  onEditNote,
  onShowHistory,
  quickSaveChild = null,
  currentScores,
  currentNotes,
}) {
  const getAnimationClasses = () => {
    if (!direction) return "";
    return `${direction === "left" ? "-translate-x-full opacity-0" : ""} 
            ${direction === "right" ? "translate-x-full opacity-0" : ""}`;
  };

  return (
    <div className="flex gap-6 w-full mb-16">
      {/* Personal Information */}
      <div className="w-1/4">
        <div
          className={`bg-card rounded-xl shadow-xl overflow-hidden transform transition-all duration-300 ${getAnimationClasses()}`}
        >
          <div className="bg-muted p-6">
            <h2 className="text-3xl font-bold text-foreground mb-3 flex items-center overflow-hidden">
              {currentApplication?.candidate?.name || "Not Found"}
              {currentApplication?.priority > 0 && (
                <span className="text-yellow-400 ml-2 flex items-center">
                  <Star className="w-4 h-4" fill="currentColor" />
                </span>
              )}
            </h2>

            <ApplicationTags
              applicationId={currentApplication?.id}
              tags={tagsPerApplication[currentApplication?.id] || []}
              availableTags={availableTags}
              onAddTag={onAddTag}
              onRemoveTag={onRemoveTag}
            />
          </div>

          <div className="p-6 space-y-6">
            <ApplicationFields
              applicationFields={currentApplication?.application_fields}
              customFields={currentVacancy?.custom_fields?.personal ?? []}
            />

            <ApplicationHistory
              currentApplicationId={currentApplication?.id}
              previousApplications={currentApplication?.candidate?.applications}
              onShowHistory={onShowHistory}
            />

            {/* Contact Section */}
            <div className="bg-card rounded-lg border border-border p-3">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-foreground break-words">
                  <Mail className="w-5 h-5" />
                  <span className="break-all">
                    {currentApplication?.candidate?.email}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {currentApplication?.application_fields?.map((field, index) => {
                if (
                  field.field_type === "file" &&
                  field.form_type === "personal"
                ) {
                  return (
                    <button
                      key={index}
                      onClick={() => window.open("#", "_blank")}
                      className="w-full bg-[#7ecfae] hover:bg-primary-dark text-white py-3 px-6 rounded-lg shadow-md transition-colors duration-200 flex items-center justify-center gap-2"
                    >
                      <FileText className="w-5 h-5" />
                      View {field.field_name}
                    </button>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>
      </div>

      {/* CV and Job Details */}
      <div className="w-2/4">
        <div
          className={`transform transition-all duration-300 ${getAnimationClasses()}`}
        >
          <JobDetail
            showCV={showCV}
            onToggleView={onToggleCV}
            resumeEnabled={currentVacancy?.resume_enable === 1}
            cvUrl={currentApplication?.resume}
          >
            <CandidateCV
              showCV={showCV}
              cvUrl={currentApplication?.resume}
              coverLetter={currentApplication?.cover_letter}
              applicationFields={currentApplication?.application_fields}
              coverLetterEnabled={currentVacancy?.cover_letter_enable === 1}
            />
          </JobDetail>
        </div>
        {showCV && currentApplication?.resume && (
          <div className="mt-6">
            <a
              href={currentApplication?.resume}
              download
              className="inline-block px-4 py-2 bg-primary-dark hover:scale-105 text-white rounded-lg transition-colors w-full text-center"
            >
              Download CV
            </a>
          </div>
        )}
      </div>

      {/* Candidate Score and Notes */}
      <div className="w-1/4">
        <div
          className={`rounded-xl overflow-hidden  transition-all duration-300 ${getAnimationClasses()}`}
        >
          <CandidateScore
            currentApplication={currentApplication}
            currentVacancy={currentVacancy}
            onScoreChange={onScoreChange}
            currentScores={currentScores}
          />

          <CandidateNotes
            key={currentApplication?.id}
            applicationId={currentApplication?.id}
            candidateId={
              currentApplication?.candidate?.id ||
              currentApplication?.candidate_id
            }
            notes={currentNotes || currentApplication?.application_notes || []}
            onAddNote={onAddNote}
            onEditNote={onEditNote}
          />
          {quickSaveChild && quickSaveChild({ className: "mt-4" })}
        </div>
      </div>
    </div>
  );
}

export default CurrentApplicationView;
